import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MultiSelect } from "primereact/multiselect";
import { Controller, useForm } from "react-hook-form";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { classNames as cx } from "primereact/utils";

import { LabelValue } from "../../../../../LabelValue/LabelValue";
import { requests } from "../../../../../../services/http-common";

import classes from "./CreateGroupForm.module.scss";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

type TForm = {
  groupName: string;
  supervizorZoneId: number | null;
  employeeIds: string[];
};

type CreateGroupFormProps = {
  onSubmit: (data: TForm) => void;
  onBackClick: () => void;
};

const schema = yup
  .object({
    groupName: yup.string().required("Field is required"),
    supervizorZoneId: yup.number().required("Field is required"),
    employeeIds: yup.array(),
  })
  .required();

const findZoneEmployees = (
  zoneId: number,
  zones: { result: { id: number; employees?: { id: string }[] }[] }
): string[] | undefined => {
  const zone = zones.result.find((z) => zoneId === z.id);
  return zone?.employees?.map((e) => e.id);
};

export const CreateGroupForm = ({
  onSubmit,
  onBackClick,
}: CreateGroupFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TForm>({
    resolver: yupResolver(schema),
  });

  const { data: employeesPayload } = useQuery({
    queryKey: ["employee-list"],
    queryFn: () => {
      return requests.get("/user");
    },
  });

  const { data: supervizorZonesPayload } = useQuery({
    queryKey: ["supervizor-zones"],
    queryFn: () => {
      return requests.get("/job-zone");
    },
  });

  const employeeOptions = useMemo(() => {
    if (!employeesPayload) return [];
    return employeesPayload.result
      .map((option: any) => ({
        label: `${option.firstName} ${option.lastName}`,
        value: option.id,
        role: option.role,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [employeesPayload]);

  const supervizorZones = useMemo(() => {
    if (!supervizorZonesPayload) return [];
    return supervizorZonesPayload.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [supervizorZonesPayload]);

  const createGroup = () => {
    handleSubmit((data) => {
      onSubmit(data);
    })();
  };

  return (
    <div className={classes.createGroupForm}>
      <div className={classes.header}>
        <i
          onClick={onBackClick}
          className="pi pi-angle-left"
          style={{ fontSize: "24px", cursor: "pointer" }}
        />
        <h3>New Group</h3>
        <Button
          label="Create"
          className="p-button-outlined p-button-secondary"
          onClick={createGroup}
        />
      </div>
      <LabelValue label="Group name" isRequired>
        <Controller
          name="groupName"
          render={({ field }) => (
            <InputText
              {...field}
              className={cx({
                "p-invalid": errors.groupName,
              })}
            />
          )}
          control={control}
        />
        {errors.groupName && (
          <small className="p-error">{errors.groupName.message}</small>
        )}
      </LabelValue>
      <LabelValue label="Service Division" isRequired>
        <Controller
          name="supervizorZoneId"
          render={({ field }) => (
            <Dropdown
              {...field}
              appendTo="self"
              options={supervizorZones}
              placeholder="Select Service Division"
              style={{ margin: "5px 0", width: "100%" }}
              className={cx({
                "p-invalid": errors.supervizorZoneId,
              })}
              onChange={(selectedValue: DropdownChangeParams) => {
                field.onChange(selectedValue);
                const zoneId = selectedValue.target.value;
                setValue(
                  "employeeIds",
                  findZoneEmployees(zoneId, supervizorZonesPayload) as string[]
                );
              }}
            />
          )}
          control={control}
        />
        {errors.supervizorZoneId && (
          <small className="p-error">{errors.supervizorZoneId.message}</small>
        )}
      </LabelValue>
      <LabelValue label="Employees" isRequired>
        <Controller
          name="employeeIds"
          render={({ field }) => (
            <MultiSelect
              {...field}
              appendTo="self"
              options={employeeOptions}
              placeholder="Select employees"
              style={{ margin: "5px 0", width: "100%" }}
              maxSelectedLabels={2}
              filter
              disabled={!field.value}
            />
          )}
          control={control}
        />
        {errors.employeeIds && (
          <small className="p-error">{errors.employeeIds.message}</small>
        )}
      </LabelValue>
    </div>
  );
};
